<template>
  <span v-if="isAdmin" class="admin-signup-alert">
    <b-nav-item-dropdown
      class="dropdown-notification mr-25"
      menu-class="dropdown-menu-media"
      right
      @show="markSeen"
    >
      <template #button-content>
        <feather-icon
          icon="UserPlusIcon"
          size="21"
          class="text-body"
          :badge="unseenCount > 0 ? String(unseenCount) : ''"
          badge-classes="bg-success"
        />
      </template>

      <li class="dropdown-menu-header">
        <div class="dropdown-header d-flex">
          <h4 class="notification-title mb-0 mr-auto">Novos Cadastros</h4>
          <b-badge pill variant="light-success">{{ recentSignups.length }}</b-badge>
        </div>
      </li>

      <vue-perfect-scrollbar
        :settings="{ maxScrollbarLength: 60, wheelPropagation: false }"
        class="scrollable-container media-list scroll-area"
        tagname="li"
        style="max-height:320px"
      >
        <b-link v-for="s in recentSignups" :key="s.id" @click="goToUsers">
          <b-media>
            <template #aside>
              <b-avatar size="32" variant="light-success">
                <feather-icon icon="UserIcon" />
              </b-avatar>
            </template>
            <p class="media-heading mb-0">
              <span class="font-weight-bolder">{{ s.user_name }} {{ s.user_sobrenome || '' }}</span>
            </p>
            <small class="notification-text">{{ s.user_email }}</small><br>
            <small class="text-muted" style="font-size:0.7rem">{{ timeAgo(s.created_at) }}</small>
          </b-media>
        </b-link>

        <div v-if="!recentSignups.length" class="text-center py-2 text-muted">
          <small>Nenhum cadastro recente</small>
        </div>
      </vue-perfect-scrollbar>

      <li class="dropdown-menu-footer">
        <b-button variant="success" block @click="goToUsers">Ver todos os usuarios</b-button>
      </li>
    </b-nav-item-dropdown>

  </span>
</template>

<script>
import {
  BNavItemDropdown, BBadge, BMedia, BLink, BAvatar, BButton,
} from 'bootstrap-vue'
import VuePerfectScrollbar from 'vue-perfect-scrollbar'

export default {
  name: 'AdminSignupAlert',
  components: { BNavItemDropdown, BBadge, BMedia, BLink, BAvatar, BButton, VuePerfectScrollbar },
  data() {
    return {
      recentSignups: [],
      unseenCount: 0,
      lastSeenId: 0,
      lastPolledMaxId: 0,
      pollInterval: null,
    }
  },
  computed: {
    isAdmin() {
      try {
        const ud = JSON.parse(localStorage.getItem('userData'))
        return ud && ud.user_role === 'admin'
      } catch { return false }
    },
  },
  mounted() {
    if (!this.isAdmin) return
    this.lastSeenId = Number(localStorage.getItem('admin_last_seen_signup') || 0)
    // Primeiro poll silencioso (só carrega dados, sem som)
    this.initPoll()
    this.pollInterval = setInterval(() => this.poll(), 30000)
  },
  beforeDestroy() {
    if (this.pollInterval) clearInterval(this.pollInterval)
  },
  methods: {
    async initPoll() {
      try {
        const token = localStorage.getItem('accessToken')
        const resp = await fetch('/api/dkbackend/admin/recent-signups', {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        })
        if (!resp.ok) return
        const data = await resp.json()
        if (!data || !data.length) return
        this.lastPolledMaxId = Math.max(...data.map(s => s.id))
        this.recentSignups = data
        this.unseenCount = data.filter(s => s.id > this.lastSeenId).length
      } catch {}
    },
    async poll() {
      try {
        const token = localStorage.getItem('accessToken')
        const resp = await fetch('/api/dkbackend/admin/recent-signups', {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        })
        if (!resp.ok) return
        const data = await resp.json()
        if (!data || !data.length) return

        const maxId = Math.max(...data.map(s => s.id))

        // Alerta apenas para leads que apareceram DESDE o ultimo poll
        if (this.lastPolledMaxId > 0 && maxId > this.lastPolledMaxId) {
          const brandNew = data.filter(s => s.id > this.lastPolledMaxId)
          if (brandNew.length) {
            this.playSound()
            this.showToast(brandNew[0])
          }
        }

        this.lastPolledMaxId = maxId
        this.recentSignups = data
        this.unseenCount = data.filter(s => s.id > this.lastSeenId).length
      } catch {}
    },
    markSeen() {
      if (this.recentSignups.length) {
        this.lastSeenId = Math.max(...this.recentSignups.map(s => s.id))
        localStorage.setItem('admin_last_seen_signup', String(this.lastSeenId))
        this.unseenCount = 0
      }
    },
    playSound() {
      try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)()
        // Two-tone notification: C5 then E5
        const notes = [523.25, 659.25]
        notes.forEach((freq, i) => {
          const osc = ctx.createOscillator()
          const gain = ctx.createGain()
          osc.type = 'sine'
          osc.frequency.value = freq
          gain.gain.setValueAtTime(0.3, ctx.currentTime + i * 0.15)
          gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + i * 0.15 + 0.3)
          osc.connect(gain)
          gain.connect(ctx.destination)
          osc.start(ctx.currentTime + i * 0.15)
          osc.stop(ctx.currentTime + i * 0.15 + 0.3)
        })
      } catch {}
    },
    showToast(signup) {
      this.$bvToast.toast(
        `${signup.user_name || ''} ${signup.user_sobrenome || ''} — ${signup.user_email}`,
        {
          title: 'Novo cadastro!',
          variant: 'success',
          solid: true,
          autoHideDelay: 8000,
          toaster: 'b-toaster-top-right',
        },
      )
    },
    timeAgo(date) {
      if (!date) return ''
      const now = Date.now()
      const d = new Date(date).getTime()
      const diff = Math.floor((now - d) / 1000)
      if (diff < 60) return 'agora'
      if (diff < 3600) return `${Math.floor(diff / 60)}min atras`
      if (diff < 86400) return `${Math.floor(diff / 3600)}h atras`
      return `${Math.floor(diff / 86400)}d atras`
    },
    goToUsers() {
      this.$router.push({ name: 'admin-usuarios' }).catch(() => {})
    },
  },
}
</script>

<style scoped>
.admin-signup-alert {
  display: inline-flex;
  align-items: center;
}
</style>
